/**
 * The `AI Closing-Note Suggestion` panel — the closing-remarks composer's own suggestion
 * surface on an **umpire** row (CC-Q17 `[Umpires]`, run 46; Alfred, 2026-09-03: *"TD also
 * needs to write reports for UMs and also a part of the summary report (top part) for all
 * umpires"*).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A SIBLING OF `AiDraftPanel`, NOT A BRANCH INSIDE IT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `AiDraftPanel` already has a correct, tested answer for an umpire row: no button, and a
 * sentence explaining that the NARRATIVE is the owning Umpires Manager's to draft (§A5, W3).
 * That answer does not change here — `ai_draft_text` on an umpire's report is still hers alone
 * to generate, and this file never touches it. What run 46 adds is a second, independent
 * suggestion for the ONE box the TD keeps on that row: the 3–4 line TD Closing Remarks. Folding
 * that into `AiDraftPanel` would give one component two unrelated buttons writing two unrelated
 * columns behind one head chip, which is exactly the kind of panel a future edit "for the AI
 * draft" could silently reach into and touch the wrong one of. Two panels, two columns, two
 * doc-comments — reusing run 45's own patterns line for line, per the brief.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SAME FOUR RULES `AiDraftPanel` KEEPS, KEPT HERE FOR THE OTHER COLUMN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **A failed press never loses the suggestion on screen.** Only a successful reply
 *     replaces `report`, exactly as `AiDraftPanel`'s does.
 *  2. **An installation with no AI service explains itself; it does not fail.** `aiConfigured`
 *     withholds the control and a sentence stands where it would have been.
 *  3. **Drawn only on an umpire row.** `report.ownedByUm` is this panel's whole gate — there is
 *     no closing note anywhere else (§B5a), so `ReportStudioPage` renders this component only
 *     where that flag is true, and the component itself asserts nothing about any other
 *     subject.
 *  4. **The suggestion is not editable here.** It is copied into the closing-remarks composer
 *     below and edited there — `Copy`, not an insert, so the generated text and the TD's own
 *     three lines stay as distinguishable as `AiDraftPanel`'s draft and `TD Draft Report` are.
 */

import { useState } from "react";

import { ApiError, generateAiClosingNote } from "../../api/client.js";
import type { ReportAiDraftBody, ReportBody } from "../../api/types.js";

/** The closing note's own target — CC-Q17's 3–4 lines, `ai/prompts.ts`'s 250–400 characters. */
const CLOSING_NOTE_LIMIT = 400;

/** Copy to the clipboard, with §B6's `execCommand` fallback for a refused permission. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Injected in tests only; the real press is `generateAiClosingNote`. */
export type AiClosingNoteGenerator = (tmsPersonId: string) => Promise<ReportAiDraftBody>;

export interface AiClosingNotePanelProps {
  /**
   * The report as fetched. Held as seed state for the reason `AiDraftPanel`'s is: the Report
   * Studio mounts this panel under a key of the official's id, so a different official is a
   * different component instance rather than a mutated one.
   */
  report: ReportBody;
  /** Told when a suggestion lands, so the page's other panels can be handed the fresh report. */
  onDrafted?: (report: ReportBody) => void;
  generate?: AiClosingNoteGenerator;
}

const GENERIC_FAILURE =
  "The suggestion could not be generated. Nothing was lost — write your closing remarks " +
  "yourself below, or try again.";

/**
 * Drawn only where `report.ownedByUm` is true — the caller decides that, not this component,
 * exactly as `ReportComposer` is handed the whole report and reads the same flag for its own
 * rendering rather than being told which mode to render in.
 */
export function AiClosingNotePanel({
  report: initialReport,
  onDrafted,
  generate = generateAiClosingNote,
}: AiClosingNotePanelProps): React.JSX.Element {
  const [report, setReport] = useState<ReportBody>(initialReport);
  const [copied, setCopied] = useState<"" | "ok" | "blocked">("");
  const [busy, setBusy] = useState<boolean>(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [truncated, setTruncated] = useState<boolean>(false);

  const text = report.aiClosingNoteText ?? "";

  async function press(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const drafted = await generate(report.tmsPersonId);
      // Only a SUCCESS replaces what is on screen — rule 1. The server wrote nothing on a
      // refusal, and this panel must not either.
      setReport(drafted.report);
      setTruncated(drafted.truncated);
      setCopied("");
      onDrafted?.(drafted.report);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : GENERIC_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="panel panel--d3"
      aria-labelledby="aiClosingNoteTitle"
      data-testid="ai-closing-note"
      data-howto="ai-closing-note"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="aiClosingNoteTitle">
          AI Closing-Note Suggestion
        </h3>
        <span className="spacer" />
        <span className="tbschip">
          <span aria-hidden="true">◆</span>Drafts From Your Quick Notes On This Umpire
        </span>
      </div>

      <div className="airep">
        <textarea
          className="aitext"
          aria-label="AI closing-note suggestion"
          data-testid="ai-closing-note-text"
          readOnly
          value={text}
          placeholder="No closing-note suggestion has been generated for this umpire yet."
        />
        <div className="aibar">
          <span className="readonly-tag">Read-Only</span>
          <span data-testid="ai-closing-note-charcount">
            {text.length} / {CLOSING_NOTE_LIMIT}
          </span>
          <span className="spacer" style={{ marginLeft: "auto" }} />
          {report.aiConfigured ? (
            <button
              type="button"
              className="gbtn"
              data-testid="ai-closing-generate"
              disabled={busy}
              onClick={() => {
                void press();
              }}
            >
              {busy ? "Drafting…" : text === "" ? "Draft Suggestion" : "Draft Again"}
            </button>
          ) : null}
          <button
            type="button"
            className="gbtn"
            data-testid="copy-ai-closing-note"
            disabled={text === ""}
            onClick={() => {
              void copyText(text).then((ok) => {
                setCopied(ok ? "ok" : "blocked");
              });
            }}
          >
            Copy
          </button>
        </div>

        {busy ? (
          <span className="btnhint" role="status" data-testid="ai-closing-note-busy">
            Reading your quick notes on this umpire — this takes a few seconds. Nothing else is
            blocked while it runs.
          </span>
        ) : null}

        {failure === null ? null : (
          <span className="btnhint" role="status" data-testid="ai-closing-note-failure">
            <b>Not generated —</b> {failure}
          </span>
        )}

        {truncated ? (
          <span className="btnhint" role="status" data-testid="ai-closing-note-truncated">
            <b>Trimmed to fit —</b> the suggestion came back longer than a closing note's
            length and was cut back at the end of a sentence. Read the last line before you use
            it.
          </span>
        ) : null}

        {report.aiConfigured ? null : (
          <span className="btnhint" data-testid="ai-closing-note-unconfigured">
            <b>AI drafting is not switched on for this installation —</b> there is no drafting
            service attached to this Control Centre, so there is nothing to press. Write your
            closing remarks in your own box below.
          </span>
        )}

        {copied === "" ? null : (
          <span className="btnhint" role="status" data-testid="ai-closing-note-copy-result">
            {copied === "ok"
              ? `Copied · ${text.length} characters on the clipboard — paste into your TD Closing Remarks below.`
              : "Copy blocked · Select the text and copy manually."}
          </span>
        )}

        <div className="hidenote">
          <b>A suggestion, never the record —</b> it is never written into TD Closing Remarks
          for you. Copy it into your own box below and edit there, so the generated text and
          your three lines stay distinguishable. It is not editable here.
        </div>
      </div>

      <div className="panel__foot">
        <b>What it is drafted from —</b> your marks for this umpire and every one of your own
        quick notes about them. The checklist follows CC-Q17&rsquo;s Umpires block — briefings
        followed, on-pitch judgment and consistency, communication with players, game
        management, rapport with the technical table — and no mark or score is ever written
        into the prose.
      </div>
    </section>
  );
}
